// Zapamietanie celu nawigacji na czas przejscia przez /login.
//
// Po co: klikniecie linku typu `/published?offerId=123` z zewnatrz (np. z notatki
// na gieldzie Trans) otwiera NOWA karte. Token portalu zyje w sessionStorage, czyli
// per karta, a mostek w localStorage ma TTL 15 min — poza tym oknem swieza karta
// zawsze przechodzi przez logowanie. Bez zapamietanego celu user ladowal na
// /dispatcher-ops i musial szukac oferty recznie.
//
// Wzorzec 1:1 z TRANS_OAUTH_PENDING_KEY: strona chroniona zapisuje, Login odczytuje
// i od razu czysci.

const RETURN_TO_KEY = 'portalReturnTo'
const MAX_LENGTH = 2048

// Znaki sterujace (\n, \r, \t, \0, ...) — nosnik sztuczek na parserach URL-i.
// Sprawdzamy po kodach, a nie regexem: regex ze znakami sterujacymi lamie
// regule eslinta no-control-regex.
const hasControlChar = (value: string): boolean => {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    if (code <= 0x1f || code === 0x7f) {
      return true
    }
  }
  return false
}

/**
 * Twarda bramka anty-open-redirect. Przepuszcza wylacznie sciezki WZGLEDNE
 * zaczynajace sie od pojedynczego "/". Wszystko inne (adres absolutny, schemat,
 * protocol-relative, backslash, traversal, znaki sterujace) leci do kosza.
 */
export function sanitizeReturnTo(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_LENGTH) {
    return null
  }
  if (hasControlChar(value)) {
    return null
  }
  // Przegladarki normalizuja "\" do "/", wiec "\\evil.com" i "/\evil.com"
  // potrafia stac sie adresem absolutnym.
  if (value.includes('\\')) {
    return null
  }
  // Tylko sciezka wzgledna: dokladnie jeden wiodacy "/".
  // Ta sama bramka odrzuca "https://evil.com" i "javascript:alert(1)".
  if (!value.startsWith('/') || value.startsWith('//')) {
    return null
  }
  // "/../", "/..", "/a/./b" — normalizacja moze wyprowadzic poza aplikacje.
  const pathname = value.split(/[?#]/, 1)[0] ?? ''
  if (pathname.split('/').some((segment) => segment === '.' || segment === '..')) {
    return null
  }
  return value
}

const readFromRouterState = (routerState: unknown): string | null => {
  if (!routerState || typeof routerState !== 'object') {
    return null
  }
  return sanitizeReturnTo((routerState as { from?: unknown }).from)
}

const readAndClearStored = (): string | null => {
  try {
    const raw = sessionStorage.getItem(RETURN_TO_KEY)
    if (raw === null) {
      return null
    }
    sessionStorage.removeItem(RETURN_TO_KEY)
    // Wartosc waliduje sie takze przy ODCZYCIE — do sessionStorage moze wpisac
    // cokolwiek obcy kod na tej samej domenie.
    return sanitizeReturnTo(raw)
  } catch {
    // sessionStorage niedostepny (tryb prywatny / zablokowane ciasteczka).
    return null
  }
}

// StrictMode renderuje komponent dwa razy, a `takeReturnTo` konsumuje wartosc.
// Zeby drugi przebieg tego samego renderu nie zobaczyl juz pustki, ostatnio zdjeta
// wartosc zostaje w pamieci do konca biezacej kolejki mikrozadan — czyli na czas
// jednego przebiegu renderu, nie dluzej.
let replayed: string | null = null
let replayScheduled = false

const scheduleReplayReset = () => {
  if (replayScheduled) {
    return
  }
  replayScheduled = true
  queueMicrotask(() => {
    replayed = null
    replayScheduled = false
  })
}

/**
 * Zapamietuje cel, na ktory user ma wrocic po zalogowaniu.
 * Zapis jest idempotentny (setItem tego samego klucza), wiec podwojne wywolanie
 * z renderu w StrictMode jest bezpieczne.
 */
export function rememberReturnTo(pathWithSearch: string): void {
  const safe = sanitizeReturnTo(pathWithSearch)
  if (!safe) {
    return
  }
  try {
    sessionStorage.setItem(RETURN_TO_KEY, safe)
  } catch {
    // Brak sessionStorage — cel przepadnie, ale samo logowanie dziala dalej.
  }
}

/**
 * Zdejmuje zapamietany cel (czyta I usuwa). Dwa kanaly, bo kazdy ma inna slabosc:
 * sessionStorage przezywa nawigacje, ale nie tryb prywatny; `state` routera
 * przezywa wszystko poza twardym reloadem (np. asset recovery). Bierzemy pierwszy
 * dostepny.
 */
export function takeReturnTo(routerState?: unknown): string | null {
  const candidate = readAndClearStored() ?? readFromRouterState(routerState)
  if (candidate) {
    replayed = candidate
    scheduleReplayReset()
    return candidate
  }
  return replayed
}
